/**
 * Figma-compliant Pagination Component for Property Table
 * Matches exact Figma design specifications with red theme
 */

"use client";

import { useState, useEffect } from "react";

import { Button, Input, Select, SelectItem } from "@nextui-org/react";
import { ChevronLeft, ChevronRight, MoreHorizontal } from "lucide-react";
import { useTranslations } from "next-intl";

import { cn } from "@/utils/cn";

import styles from "./FigmaPagination.module.scss";

export interface FigmaPaginationProps {
  /** Current page (1-based) */
  currentPage: number;
  /** Total number of pages */
  totalPages: number;
  /** Page change handler */
  onPageChange: (page: number) => void;
  /** Current page size */
  pageSize: number;
  /** Page size change handler */
  onPageSizeChange: (size: number) => void;
  /** Total number of items */
  totalItems: number;
  /** Available page sizes */
  pageSizeOptions?: readonly number[];
  /** Component className */
  className?: string;
  /** Show page size selector */
  showPageSizeSelector?: boolean;
  /** Show item count info */
  showItemInfo?: boolean;
  /** Maximum visible page buttons */
  maxVisiblePages?: number;
}

export function FigmaPagination({
  currentPage,
  totalPages,
  onPageChange,
  pageSize,
  onPageSizeChange,
  totalItems,
  pageSizeOptions = [10, 20, 25, 50, 100],
  className,
  showPageSizeSelector = true,
  showItemInfo = true,
  maxVisiblePages = 7,
}: FigmaPaginationProps) {
  const t = useTranslations("search");
  const [goToValue, setGoToValue] = useState<string>("");
  const [isSmallScreen, setIsSmallScreen] = useState(false);

  // Check screen size
  useEffect(() => {
    const checkScreenSize = () => {
      setIsSmallScreen(window.innerWidth <= 391);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Calculate visible page numbers
  const getVisiblePages = () => {
    const pages: (number | "ellipsis")[] = [];

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is less than max
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Complex pagination with ellipsis
      const sidePages = Math.floor((maxVisiblePages - 3) / 2); // Reserve space for 1, ..., last

      if (currentPage <= sidePages + 2) {
        // Near the beginning
        for (let i = 1; i <= sidePages + 3; i++) {
          pages.push(i);
        }
        if (sidePages + 4 < totalPages) {
          pages.push("ellipsis");
        }
        if (sidePages + 3 < totalPages) {
          pages.push(totalPages);
        }
      } else if (currentPage >= totalPages - sidePages - 1) {
        // Near the end
        pages.push(1);
        if (totalPages - sidePages - 3 > 1) {
          pages.push("ellipsis");
        }
        for (let i = totalPages - sidePages - 2; i <= totalPages; i++) {
          if (i > 1) pages.push(i);
        }
      } else {
        // In the middle
        pages.push(1);
        if (currentPage - sidePages > 2) {
          pages.push("ellipsis");
        }
        for (
          let i = currentPage - sidePages;
          i <= currentPage + sidePages;
          i++
        ) {
          if (i > 1 && i < totalPages) {
            pages.push(i);
          }
        }
        if (currentPage + sidePages < totalPages - 1) {
          pages.push("ellipsis");
        }
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const visiblePages = getVisiblePages();

  // Calculate item range
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const handlePageClick = (page: number) => {
    if (page !== currentPage && page >= 1 && page <= totalPages) {
      onPageChange(page);
    }
  };

  if (totalPages <= 1 && !showPageSizeSelector && !showItemInfo) {
    return null;
  }

  return (
    <div className={cn(styles.figmaPagination, className)}>
      {/* Left side - Item info and page size */}
      <div className={styles.leftSection}>
        {showItemInfo && (
          <span className={styles.itemInfo}>
            {t("tablePaginationShowing", {
              start: startItem,
              end: endItem,
              total: totalItems,
            }) || `Showing ${startItem}-${endItem} of ${totalItems}`}
          </span>
        )}

        {showPageSizeSelector && (
          <div
            className={styles.pageSizeSelector}
            style={isSmallScreen ? {
              width: '100%',
              flexDirection: 'column',
              gap: '8px',
              alignItems: 'stretch'
            } : {}}
          >
            <span
              className={styles.pageSizeLabel}
              style={isSmallScreen ? { textAlign: 'center' } : {}}
            >
              {t("tablePaginationPageSize") || "Page size"}:
            </span>

            {/* Show buttons on small screens */}
            {isSmallScreen ? (
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '4px',
                justifyContent: 'center'
              }}>
                {pageSizeOptions.map((size) => (
                  <button
                    key={size}
                    onClick={() => onPageSizeChange(size)}
                    style={{
                      padding: '6px 12px',
                      fontSize: '14px',
                      borderRadius: '6px',
                      border: '1px solid',
                      transition: 'all 0.2s ease',
                      backgroundColor: pageSize === size ? '#cf1409' : 'white',
                      color: pageSize === size ? 'white' : '#374151',
                      borderColor: pageSize === size ? '#cf1409' : '#d1d5db',
                      cursor: 'pointer',
                      fontFamily: 'Geometria, -apple-system, BlinkMacSystemFont, sans-serif'
                    }}
                    onMouseEnter={(e) => {
                      if (pageSize !== size) {
                        e.currentTarget.style.borderColor = '#cf1409';
                        e.currentTarget.style.color = '#cf1409';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (pageSize !== size) {
                        e.currentTarget.style.borderColor = '#d1d5db';
                        e.currentTarget.style.color = '#374151';
                      }
                    }}
                  >
                    {size}
                  </button>
                ))}
              </div>
            ) : (
              /* Show select on larger screens */
              <Select
                size="sm"
                variant="bordered"
                selectedKeys={[String(pageSize)]}
                onSelectionChange={(keys) => {
                  const size = Number(Array.from(keys)[0]);
                  if (size && size !== pageSize) {
                    onPageSizeChange(size);
                  }
                }}
                className={styles.pageSizeSelect}
                classNames={{
                  trigger: styles.pageSizeTrigger,
                  value: styles.pageSizeValue,
                  popoverContent: styles.pageSizePopover,
                }}
                aria-label={t("tablePaginationPageSize") || "Page size"}
              >
                {pageSizeOptions.map((size) => (
                  <SelectItem
                    key={String(size)}
                    value={String(size)}
                  >
                    {String(size)}
                  </SelectItem>
                ))}
              </Select>
            )}
          </div>
        )}
      </div>

      {/* Right side - Pagination controls */}
      {totalPages > 1 && (
        <div className={styles.rightSection}>
          <div className={styles.paginationControls}>
            {/* Previous button */}
            <Button
              size="sm"
              variant="ghost"
              isDisabled={currentPage <= 1}
              onPress={handlePreviousPage}
              className={cn(styles.navButton, styles.prevButton)}
              startContent={<ChevronLeft size={16} />}
            >
              Previous
            </Button>

            {/* Page numbers */}
            <div className={styles.pageNumbers}>
              {visiblePages.map((page, index) => {
                if (page === "ellipsis") {
                  return (
                    <div
                      key={`ellipsis-${index}`}
                      className={styles.ellipsis}
                    >
                      <MoreHorizontal size={16} />
                    </div>
                  );
                }

                const isActive = page === currentPage;

                return (
                  <Button
                    key={page}
                    size="sm"
                    variant={isActive ? "solid" : "ghost"}
                    color={isActive ? "primary" : "default"}
                    onPress={() => handlePageClick(page)}
                    className={cn(
                      styles.pageButton,
                      isActive && styles.pageButtonActive,
                      isActive && styles.pageButtonActiveBase,
                    )}
                  >
                    {page}
                  </Button>
                );
              })}
            </div>

            {/* Next button */}
            <Button
              size="sm"
              variant="ghost"
              isDisabled={currentPage >= totalPages}
              onPress={handleNextPage}
              className={cn(styles.navButton, styles.nextButton)}
              endContent={<ChevronRight size={16} />}
            >
              Next
            </Button>
          </div>

          {/* Go To Page */}
          <div className={styles.pageSizeSelector}>
            <span className={styles.pageSizeLabel}>
              {t("tablePaginationGoTo") || "Перейти на страницу"}
            </span>
            <Input
              size="sm"
              type="number"
              variant="bordered"
              value={goToValue}
              onValueChange={setGoToValue}
              aria-label={t("tablePaginationEnterNumber") || "Введите номер"}
              classNames={{
                inputWrapper: styles.pageSizeTrigger,
                input: styles.pageSizeValue,
              }}
              placeholder={t("tablePaginationEnterNumber") || "Введите номер"}
              min={1}
              max={totalPages}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const num = Number(goToValue);
                  if (Number.isFinite(num) && num >= 1 && num <= totalPages) {
                    onPageChange(num);
                  }
                }
              }}
              className="w-24"
            />
            <Button
              size="sm"
              variant="ghost"
              onPress={() => {
                const num = Number(goToValue);
                if (Number.isFinite(num) && num >= 1 && num <= totalPages) {
                  onPageChange(num);
                }
              }}
            >
              OK
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default FigmaPagination;
